import {parsePhoneNumberFromString} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import {isoCountryToPrefix} from '../../components/Dialer/services/countryDataService';
import type {
    Destination,
    DestinationCategory,
    DestinationType,
    RuleAction,
    RulePurpose,
    ScopeMode,
} from '../../store/features/CallRestrictions/callRestrictionsTypes';

export const DESTINATION_TYPE_LABEL: Record<DestinationType, string> = {
    COUNTRY: 'Country',
    PREFIX: 'Starts with',
    NUMBER: 'Exact number',
    RANGE: 'Number range',
    CATEGORY: 'Category',
};

export const MODE_LABEL: Record<ScopeMode, string> = {
    BLOCK_LIST: 'Block list',
    ALLOW_LIST: 'Allow list',
};

export const ACTION_LABEL: Record<RuleAction, string> = {
    ALLOW: 'Allow',
    BLOCK: 'Block',
};

export const digitsOf = (value: unknown): string => String(value ?? '').replace(/\D/g, '');

/** `+48 22 100 20 30` for full numbers, `+4870` for anything libphonenumber can't format. */
export const formatE164 = (digits: string | undefined): string => {
    const clean = digitsOf(digits);
    if (!clean) return '';
    const parsed = parsePhoneNumberFromString(`+${clean}`);
    return parsed?.isValid() ? parsed.formatInternational() : `+${clean}`;
};

export const countryName = (iso: string | undefined): string => {
    if (!iso) return '';
    const code = iso.toUpperCase();
    try {
        return new Intl.DisplayNames(['en'], {type: 'region'}).of(code) ?? code;
    } catch {
        return code;
    }
};

export const findCategory = (categories: DestinationCategory[], id: string | undefined) =>
    categories.find((category) => category.id === id);

export interface DestinationText {
    name: string;
    detail: string;
}

/** Name and detail lines of a destination, e.g. "Russia" / "Country · +7". */
export const describeDestination = (
    destination: Destination,
    categories: DestinationCategory[],
    label?: string | null,
): DestinationText => {
    switch (destination.type) {
        case 'COUNTRY': {
            const prefix = isoCountryToPrefix(destination.value ?? '');
            return {
                name: label || countryName(destination.value),
                detail: prefix ? `Country · ${prefix}` : 'Country',
            };
        }
        case 'PREFIX':
            return {
                name: label || `Numbers starting with +${digitsOf(destination.value)}`,
                detail: `Prefix · +${digitsOf(destination.value)}`,
            };
        case 'RANGE':
            return {
                name: label || `${formatE164(destination.from)} – ${formatE164(destination.to)}`,
                detail: 'Number range',
            };
        case 'CATEGORY': {
            const category = findCategory(categories, destination.value);
            const prefixes = category?.prefixes.length ? ` · ${category.prefixes.map((p) => `+${p}`).join(', ')}` : '';
            return {
                name: label || category?.name || destination.value || 'Category',
                detail: `Category${prefixes}`,
            };
        }
        case 'NUMBER':
        default:
            return {name: label || formatE164(destination.value), detail: 'Number'};
    }
};

export const appliesToText = (appliesTo: RulePurpose[]): string => {
    const call = appliesTo.includes('CALL');
    const forward = appliesTo.includes('FORWARD');
    if (call && forward) return 'Calls and forwarding';
    if (call) return 'Calls only';
    if (forward) return 'Forwarding only';
    return '—';
};

/** Countries sharing this calling code, main country first (libphonenumber metadata). */
export const countriesForCallingCode = (digits: string): string[] =>
    ((metadata as {country_calling_codes: Record<string, string[]>}).country_calling_codes[digits] ?? []).map(countryName);

/** The hint under the "Numbers starting with" field. */
export const prefixHint = (input: string): string => {
    const digits = digitsOf(input);
    const format = 'Use the international format, starting with +.';
    if (!digits) return format;
    const countries = countriesForCallingCode(digits);
    if (digits === '1') return `Matches every number with calling code +1: the United States, Canada and the Caribbean. ${format}`;
    if (countries.length === 2) return `Matches every number in ${countries[0]} and ${countries[1]}. ${format}`;
    // One country, or a main country with small territories (+44 is also Guernsey, Jersey and the Isle of Man).
    if (countries.length > 0) return `Matches every number in ${countries[0]}. ${format}`;
    return `Matches numbers starting with +${digits}. ${format}`;
};

export interface DestinationDraft {
    type: DestinationType;
    country: string;
    prefix: string;
    number: string;
    rangeFrom: string;
    rangeTo: string;
    category: string;
}

export const EMPTY_DRAFT: DestinationDraft = {
    type: 'COUNTRY',
    country: '',
    prefix: '',
    number: '',
    rangeFrom: '',
    rangeTo: '',
    category: '',
};

export const draftOf = (destination: Destination): DestinationDraft => ({
    ...EMPTY_DRAFT,
    type: destination.type,
    country: destination.type === 'COUNTRY' ? destination.value ?? '' : '',
    prefix: destination.type === 'PREFIX' ? `+${digitsOf(destination.value)}` : '',
    number: destination.type === 'NUMBER' ? `+${digitsOf(destination.value)}` : '',
    rangeFrom: destination.type === 'RANGE' ? `+${digitsOf(destination.from)}` : '',
    rangeTo: destination.type === 'RANGE' ? `+${digitsOf(destination.to)}` : '',
    category: destination.type === 'CATEGORY' ? destination.value ?? '' : '',
});

/** Shared rule for typed numbers: must start with + so we never guess the country. */
const numberError = (input: string): string | undefined => {
    const trimmed = input.trim();
    if (!trimmed) return 'Enter a number.';
    if (!trimmed.startsWith('+')) return 'Use the international format, starting with +.';
    const digits = digitsOf(trimmed);
    if (digits.length < 3 || digits.length > 15) return 'Enter a number with 3 to 15 digits.';
    return undefined;
};

export type DraftResult = {destination: Destination; error?: undefined} | {destination?: undefined; error: string};

/** Turns the panel's fields into a contract destination (digits without +), or says what's wrong. */
export const destinationFromDraft = (draft: DestinationDraft): DraftResult => {
    switch (draft.type) {
        case 'COUNTRY':
            return draft.country ? {destination: {type: 'COUNTRY', value: draft.country.toUpperCase()}} : {error: 'Choose a country.'};
        case 'PREFIX': {
            const trimmed = draft.prefix.trim();
            if (!trimmed.startsWith('+') || !digitsOf(trimmed)) return {error: 'Enter the first digits, starting with +.'};
            if (digitsOf(trimmed).length > 15) return {error: 'A prefix can have at most 15 digits.'};
            return {destination: {type: 'PREFIX', value: digitsOf(trimmed)}};
        }
        case 'NUMBER': {
            const error = numberError(draft.number);
            return error ? {error} : {destination: {type: 'NUMBER', value: digitsOf(draft.number)}};
        }
        case 'RANGE': {
            const error = numberError(draft.rangeFrom) ?? numberError(draft.rangeTo);
            if (error) return {error};
            const from = digitsOf(draft.rangeFrom);
            const to = digitsOf(draft.rangeTo);
            if (from.length !== to.length) return {error: 'Both ends of the range need the same number of digits.'};
            if (from > to) return {error: 'The first number must come before the last one.'};
            return {destination: {type: 'RANGE', from, to}};
        }
        case 'CATEGORY':
            return draft.category ? {destination: {type: 'CATEGORY', value: draft.category}} : {error: 'Choose a category.'};
    }
};

/** Does a PhoneHQ block cover part of this destination? Used for the "stays blocked" note. */
export const overlappingPrefixes = (destination: Destination, blockedPrefixes: string[]): string[] => {
    const own = destination.type === 'PREFIX'
        ? digitsOf(destination.value)
        : destination.type === 'COUNTRY'
            ? digitsOf(isoCountryToPrefix(destination.value ?? '') ?? '')
            : '';
    if (!own) return [];
    return blockedPrefixes.filter((blocked) => blocked.length > own.length && blocked.startsWith(own));
};

/** Prefixes covered by a destination, for overlap checks. Ranges and numbers aren't prefixes. */
export const prefixesOf = (destination: Destination, categories: DestinationCategory[]): string[] => {
    switch (destination.type) {
        case 'PREFIX':
            return [digitsOf(destination.value)];
        case 'COUNTRY': {
            const prefix = digitsOf(isoCountryToPrefix(destination.value ?? '') ?? '');
            return prefix ? [prefix] : [];
        }
        case 'CATEGORY':
            return findCategory(categories, destination.value)?.prefixes ?? [];
        default:
            return [];
    }
};
